/**
 * AI 智能分类 · UI 切片
 *
 * 维护纯 UI 态（与服务端无关）：
 *  · 侧边栏开关 / 宽度
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'

/** 侧边栏默认宽度 · 与 AIClassifyPage.module.scss 的 flex-basis 保持同步 */
const DEFAULT_SIDEBAR_WIDTH = 420

/** 列表页视图模式 */
export type ListViewMode = 'grid' | 'list'

/** 列表页排序 · 列表 / 宫格视图共用 · 列名与 FolderListHeader 同步
 *
 * ctime · 文件夹创建时间 · adaptFolder.ts 已从 raw.ctime / raw.createTime 归一为秒级数字
 *  · 仅在头部排序下拉 / 右键菜单出现，列表视图表头不展示该列（节省宽度） */
export type FolderSortKey =
  | 'folderName'
  | 'photoCount'
  | 'subfolderCount'
  | 'updateTime'
  | 'ctime'
export type FolderSortOrder = 'asc' | 'desc'
export interface FolderSort {
  key: FolderSortKey
  order: FolderSortOrder
}

/**
 * Composer 顶部「当前文件夹」上下文 · 仅文件夹详情页有值，列表页为 null
 *
 *  · folderId · 当前文件夹 ID（string，来自路由 :folderId）· 进详情页即可拿到。
 *    预留作会话上下文：后端 send 接口支持后随消息一起带上（当前仅存不发）。
 *  · path · 文件夹名路径「A/B/当前」（不含根「照片分类」）· 仅用于路径条展示。
 *    需等 getFolderInfo 拿到 parentFolders + folderName 才有值，比 folderId 晚到。
 */
export interface ComposerFolderContext {
  folderId: string
  path: string
}

interface UIState {
  sidebarOpen: boolean
  /**
   * 侧边栏默认开合是否已决定 · 防止"按列表内容定默认值"覆盖用户操作
   *
   * 列表页首次加载完成后按"有无文件夹"应用一次默认值（applySidebarDefault）；
   * 用户/业务流手动开合（open/close/toggleSidebar）也会锁定，此后默认值不再介入
   */
  sidebarDefaultApplied: boolean
  sidebarWidth: number
  /**
   * 列表页"多选模式"开关 · true 时进入多选态（卡片显示勾选 + 底部操作条）
   *
   * 与 plan 选中态分开：plan 选的是 PlanFolder.id（前端临时 id），
   * 这里选的是真实 folderID（FolderListItem.folderId / 后端 folderID）。
   *
   * 顶部只一个「多选」按钮入口；底部操作条同时给「下载」+「删除」两个按钮，
   * 用户在多选状态下自由选择对选中项做哪种动作 —— 不再用 manageAction 区分入口。
   */
  manageMode: boolean
  /** 多选模式下勾选的真实 folderID 集合 */
  manageSelectedFolderIds: string[]
  /** 列表视图模式 · grid（宫格，默认）/ list（行）；放 redux 让顶部切换按钮和列表共享 */
  viewMode: ListViewMode
  /**
   * 文件夹列表排序 · 宫格 / 列表视图共用 · 由头部「排序」按钮 + 列表视图表头切换
   * 默认更新时间倒序，会话内保持，跨页面不持久化
   */
  folderSort: FolderSort
  /**
   * 历史会话覆盖层开关 · 抽屉头部「历史会话」按钮触发
   *  · 旧版本走 antd Popover · 现改为从 AIChat 容器底部 slide-up 的全幅 overlay
   *  · 与"新对话"动画互斥（同时只有一个 overlay 在动）
   */
  historyOverlayOpen: boolean
  /**
   * "新对话"过渡动画 seq · 每次开新对话 +1 触发一次 slide-up 覆盖动画
   *  · AIChat 用它当 key，使内层包装重挂载并播 CSS 入场动画
   *  · 不依赖时间，只依赖序列变化 · 切换历史会话不触发
   */
  newConvAnimSeq: number
  /**
   * Composer 顶部「当前文件夹」上下文 · 仅文件夹详情页有值，列表页为 null
   *  · 详情页进入时写入、离开时清除（见 Detail/index.tsx）
   *  · 列表页与详情页共用同一套 AIChat / Composer，靠这个字段区分是否展示路径条
   */
  composerFolderContext: ComposerFolderContext | null
}

const initialState: UIState = {
  // 初始收起 · 列表首次加载成功后由 applySidebarDefault 决定默认开合：
  // 有文件夹默认展开，空列表保持收起（空态有自己的 AI 入口卡片）
  sidebarOpen: false,
  sidebarDefaultApplied: false,
  sidebarWidth: DEFAULT_SIDEBAR_WIDTH,
  manageMode: false,
  manageSelectedFolderIds: [],
  viewMode: 'grid',
  folderSort: { key: 'updateTime', order: 'desc' },
  historyOverlayOpen: false,
  newConvAnimSeq: 0,
  composerFolderContext: null
}

const uiSlice = createSlice({
  name: 'aiClassify/ui',
  initialState,
  reducers: {
    openSidebar(state) {
      state.sidebarOpen = true
      state.sidebarDefaultApplied = true
    },
    closeSidebar(state) {
      state.sidebarOpen = false
      state.sidebarDefaultApplied = true
    },
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen
      state.sidebarDefaultApplied = true
    },
    /**
     * 按"有无文件夹"应用侧边栏默认开合（payload = 是否有文件夹）。
     * 只在默认值未决定时生效一次：有文件夹 → 展开；空列表 → 维持收起。
     * 列表页首次加载成功后派发；后台刷新重复派发会被标记挡掉，幂等。
     */
    applySidebarDefault(state, action: PayloadAction<boolean>) {
      if (state.sidebarDefaultApplied) return
      state.sidebarDefaultApplied = true
      if (action.payload) state.sidebarOpen = true
    },
    setSidebarWidth(state, action: PayloadAction<number>) {
      state.sidebarWidth = action.payload
    },

    // ────────────── 列表页"多选模式" ──────────────

    /** 进入多选模式 · 清空旧选择 */
    enterManageMode(state) {
      state.manageMode = true
      state.manageSelectedFolderIds = []
    },

    /** 退出多选模式 · 操作完成 / 用户点取消 时调 */
    exitManageMode(state) {
      state.manageMode = false
      state.manageSelectedFolderIds = []
    },

    /** 切换某真实 folderID 的勾选 */
    toggleManageFolder(state, action: PayloadAction<string>) {
      const id = action.payload
      const idx = state.manageSelectedFolderIds.indexOf(id)
      if (idx >= 0) state.manageSelectedFolderIds.splice(idx, 1)
      else state.manageSelectedFolderIds.push(id)
    },

    /** 整体覆盖管理选中态 · 用于"全选 / 取消全选" */
    setManageFolderSelection(state, action: PayloadAction<string[]>) {
      state.manageSelectedFolderIds = [...action.payload]
    },

    /** 切换列表视图（grid / list） */
    setViewMode(state, action: PayloadAction<ListViewMode>) {
      state.viewMode = action.payload
    },

    /** 设置文件夹列表排序 · 头部「排序」下拉 + 列表表头都派发这个 */
    setFolderSort(state, action: PayloadAction<FolderSort>) {
      state.folderSort = action.payload
    },

    /** 历史会话 overlay 开关 · AI 助手头部「历史会话」按钮 + overlay 关闭按钮派发 */
    setHistoryOverlayOpen(state, action: PayloadAction<boolean>) {
      state.historyOverlayOpen = action.payload
    },

    /** 触发"新对话"slide-up 动画 · startNewConversation 调用一次 */
    bumpNewConvAnim(state) {
      state.newConvAnimSeq += 1
    },

    /**
     * 设置 / 清除 Composer 顶部「当前文件夹」上下文。
     *  · 进文件夹详情页：传 { folderId, path }（path 后到时再覆盖一次）
     *  · 离开详情页 / 回列表页：传 null 清除，路径条随之隐藏
     */
    setComposerFolderContext(
      state,
      action: PayloadAction<ComposerFolderContext | null>
    ) {
      state.composerFolderContext = action.payload
    }
  }
  // 切到新对话 / 切团队 时 sidebar 状态保持不动 (用户开着抽屉切对话)
  // 管理模式不受新对话影响 (与 plan 选中态独立)
})

export const {
  openSidebar,
  closeSidebar,
  toggleSidebar,
  applySidebarDefault,
  setSidebarWidth,
  enterManageMode,
  exitManageMode,
  toggleManageFolder,
  setManageFolderSelection,
  setViewMode,
  setFolderSort,
  setHistoryOverlayOpen,
  bumpNewConvAnim,
  setComposerFolderContext
} = uiSlice.actions

export default uiSlice.reducer
